<template>
    <ValidationObserver ref="observer" tag="div">
        <!-- ── Responsable 1 (obligatorio) ── -->
        <h3
            class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-4"
        >
            Datos del responsable 1
        </h3>
        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:gap-y-6">
            <BaseInputText
                v-model="formData.responsable_1_nombre"
                label="Nombre *"
                name="Nombre del responsable 1"
                rules="required"
            />
            <BaseInputText
                v-model="formData.responsable_1_apellidos"
                label="Apellidos *"
                name="Apellidos del responsable 1"
                rules="required"
            />
            <DocumentTypePicker
                :value="formData.responsable_1_tipoDocumento"
                @input="onR1TipoDocumentoChange"
            />

            <DocumentInput
                :value="formData.responsable_1_dni"
                :document-type="formData.responsable_1_tipoDocumento"
                data-cy="dni-responsable-1"
                @input="val => formData.responsable_1_dni = val"
            />
            <PhoneInput
                :value="formData.responsable_1_telefono"
                :error="r1PhoneError"
                data-cy="telefono-responsable-1"
                @input="val => { formData.responsable_1_telefono = val; r1PhoneError = ''; }"
                @update:callingCode="code => formData.responsable_1_callingCode = code"
                @update:isValid="val => r1PhoneValid = val"
            />
            <BaseInputText
                v-model="formData.responsable_1_email"
                label="Email *"
                name="Email del responsable 1"
                rules="required|email"
            />

            <BaseDateInput
                v-model="formData.responsable_1_fecha_nacimiento"
                label="Fecha de nacimiento *"
                name="Fecha de nacimiento del responsable 1"
                rules="required"
            />

            <!-- ── Custom fields configurables para responsable 1 ── -->
            <template v-if="fields && fields.length > 0">
                <div v-for="field in fields" :key="`r1-${field.name}`" class="r1-fields tw:col-span-1">
                    <FieldComponent
                        :field="field"
                        v-model="formData.fields_responsable_1[field.name]"
                    />
                </div>
            </template>
        </div>

        <!-- ── Responsable 2 (opcional) ── -->
        <h3
            class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-4 tw:mt-8"
        >
            Datos del responsable 2
        </h3>
        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:gap-y-6">
            <BaseInputText
                v-model="formData.responsable_2_nombre"
                label="Nombre"
                name="Nombre del responsable 2"
                :rules="hasResponsable2 ? 'required' : ''"
                @input="onR2Input"
            />
            <BaseInputText
                v-model="formData.responsable_2_apellidos"
                label="Apellidos"
                name="Apellidos del responsable 2"
                :rules="hasResponsable2 ? 'required' : ''"
                @input="onR2Input"
            />
            <DocumentTypePicker
                :value="formData.responsable_2_tipoDocumento"
                :rules="hasResponsable2 ? 'required' : ''"
                @input="onR2TipoDocumentoChange"
            />

            <DocumentInput
                :value="formData.responsable_2_dni"
                :document-type="formData.responsable_2_tipoDocumento"
                data-cy="dni-responsable-2"
                @input="val => formData.responsable_2_dni = val"
            />
            <PhoneInput
                :value="formData.responsable_2_telefono"
                :error="r2PhoneError"
                data-cy="telefono-responsable-2"
                @input="val => { formData.responsable_2_telefono = val; r2PhoneError = ''; }"
                @update:callingCode="code => formData.responsable_2_callingCode = code"
                @update:isValid="val => r2PhoneValid = val"
            />
            <BaseInputText
                v-model="formData.responsable_2_email"
                label="Email"
                name="Email del responsable 2"
                :rules="hasResponsable2 ? 'required|email' : ''"
                @input="onR2Input"
            />

            <BaseDateInput
                v-model="formData.responsable_2_fecha_nacimiento"
                label="Fecha de nacimiento"
                name="Fecha de nacimiento del responsable 2"
                :rules="hasResponsable2 ? 'required' : ''"
            />

            <!-- ── Custom fields configurables para responsable 2 ── -->
            <template v-if="fields && fields.length > 0">
                <div v-for="field in r2CustomFields" :key="`r2-${field.name}`" class="r2-fields tw:col-span-1">
                    <FieldComponent
                        :field="field"
                        v-model="formData.fields_responsable_2[field.name]"
                    />
                </div>
            </template>
        </div>

        <!-- ── Observaciones ── -->
        <div class="tw:mt-8">
            <BaseTextarea
                v-model="formData.observaciones"
                label="Observaciones"
                name="observaciones"
                :rows="3"
            />
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, computed, inject, watch } from 'vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseTextarea from '@/modules/admin/ui/BaseTextarea.vue';
    import BaseDateInput from '@/modules/admin/ui/BaseDateInput.vue';
    import DocumentTypePicker from '@/modules/admin/ui/DocumentTypePicker.vue';
    import DocumentInput from '@/modules/admin/ui/DocumentInput.vue';
    import PhoneInput from '@/modules/admin/ui/PhoneInput.vue';
    import FieldComponent from '@/components/common/FieldComponent.vue';

    /**
     * StepperPasoDos — Segundo paso del wizard de creación de deportistas.
     *
     * Datos de los responsables (1 obligatorio, 2 opcional) + observaciones.
     * Recibe formData vía inject (compartido desde Index.vue).
     * Sigue el mismo patrón que StepperPasoUno:
     *   - ValidationObserver para validación con VeeValidate
     *   - Componentes modernos (BaseInputText, DocumentInput, PhoneInput, etc.)
     *   - defineExpose con observer + validate() para el padre
     *
     * @prop {Boolean} submitting — Indica si se está enviando.
     * @prop {Array} fields — Campos personalizados configurables para la lista de
     *   abonados. Cada uno se renderiza como FieldComponent, con v-model hacia
     *   `formData.fields_responsable_1[field.name]` o `fields_responsable_2`.
     */
    const props = defineProps({
        submitting: { type: Boolean, default: false },
        fields: { type: Array, default: () => [] },
    });

    // ── formData inyectado desde Index.vue (NO es prop) ──
    const formData = inject('formData');

    // ── ValidationObserver ref (expuesto al padre para validación) ──
    const observer = ref(null);

    // ── Validación de teléfonos ──
    const r1PhoneValid = ref(false);
    const r2PhoneValid = ref(false);
    const r1PhoneError = ref('');
    const r2PhoneError = ref('');

    // ── Responsable 2: detectar si tiene algún dato ingresado ──
    //   IMPORTANTE: usamos formData (inject), NO props.formData (que es undefined).
    const hasResponsable2 = computed(() => {
        const d = formData;
        return !!(
            (d.responsable_2_nombre && d.responsable_2_nombre.trim()) ||
            (d.responsable_2_apellidos && d.responsable_2_apellidos.trim()) ||
            (d.responsable_2_dni && d.responsable_2_dni.trim()) ||
            (d.responsable_2_telefono && d.responsable_2_telefono.trim()) ||
            (d.responsable_2_email && d.responsable_2_email.trim()) ||
            (d.responsable_2_fecha_nacimiento && d.responsable_2_fecha_nacimiento.trim())
        );
    });

    /**
     * Custom fields para R2 con required condicional.
     * Si R2 no tiene ningún dato ingresado, forzamos required=false para que
     * esos campos no sean obligatorios. En cuanto R2 tenga datos, respetamos
     * el required del schema original.
     */
    const r2CustomFields = computed(() => {
        const fields = props.fields || [];
        if (!hasResponsable2.value) {
            return fields.map(f => ({ ...f, required: false }));
        }
        return fields;
    });

    // ── Handlers ──
    function onR1TipoDocumentoChange(val) {
        formData.responsable_1_tipoDocumento = val;
        formData.responsable_1_dni = '';
    }

    function onR2TipoDocumentoChange(val) {
        formData.responsable_2_tipoDocumento = val;
        formData.responsable_2_dni = '';
    }

    function onR2Input() {
        // Si R2 no tiene tipo de documento asignado y está recibiendo datos,
        // auto-inicializar a 'DNI' (mismo comportamiento que el legacy)
        if (hasResponsable2.value && !formData.responsable_2_tipoDocumento) {
            formData.responsable_2_tipoDocumento = 'DNI';
        }
    }

    // ── Watch: si R2 pierde todos los datos, limpiar tipoDocumento y DNI ──
    watch(hasResponsable2, tieneDatos => {
        if (!tieneDatos && formData.responsable_2_tipoDocumento) {
            formData.responsable_2_tipoDocumento = '';
            formData.responsable_2_dni = '';
        }
    });

    // ── Validación expuesta al padre ──
    //   Index.vue llama a step2Ref.value.validate()
    async function validate() {
        const isFormValid = await observer.value.validate();

        // Validar teléfono R1 (requerido)
        const r1PhoneOk = !!formData.responsable_1_telefono && r1PhoneValid.value;
        if (!r1PhoneOk) {
            r1PhoneError.value = 'El teléfono es requerido y debe ser válido';
        }

        // Validar teléfono R2 (solo si R2 tiene datos)
        let r2PhoneOk = true;
        if (hasResponsable2.value) {
            r2PhoneOk = !!formData.responsable_2_telefono && r2PhoneValid.value;
            if (!r2PhoneOk) {
                r2PhoneError.value = 'El teléfono es requerido y debe ser válido';
            }
        }

        return isFormValid && r1PhoneOk && r2PhoneOk;
    }

    // ── Exponer observer para validación directa y validate() completo ──
    //   Sigue el mismo patrón que StepperPasoUno.
    //   r2CustomFields expuesto para tests de required condicional.
    defineExpose({
        get observer() { return observer.value; },
        validate,
        get r2CustomFields() { return r2CustomFields.value; },
    });
</script>
